import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { clubApi } from "../../../club-api-client";
import type { HeatmapParams } from "../../../club-api/member-availability";
import type { SetAvailabilityPattern } from "../../../schemas/club-availability";
import { queryKeys } from "../../query-keys";

// Aggregate view of when the club is free. Owner and admin only on the
// service side; a member reading this gets a 403, so gate the call site.
export function useAvailabilityHeatmap(clubId: string | null, params?: HeatmapParams) {
	return useQuery({
		enabled: !!clubId,
		queryFn: () => clubApi.memberAvailability.heatmap(clubId!, params),
		// Every parameter that narrows the response belongs in the key —
		// skill_level_id changes both the counts and total_members, so a key
		// without it would serve one level's grid under another level's filter.
		queryKey: queryKeys.clubPortal.availabilityHeatmap(clubId ?? "", {
			from: params?.from?.toISOString(),
			granularity: params?.granularity,
			skillLevelId: params?.skillLevelId,
			to: params?.to?.toISOString(),
		}),
	});
}

export function useMyClubAvailability(clubId: string | null) {
	return useQuery({
		enabled: !!clubId,
		queryFn: () => clubApi.memberAvailability.me(clubId!),
		queryKey: queryKeys.clubPortal.availabilityMine(clubId ?? ""),
	});
}

export function useMemberClubAvailability(clubId: string | null, userId: string | null) {
	return useQuery({
		enabled: !!clubId && !!userId,
		queryFn: () => clubApi.memberAvailability.forUser(clubId!, userId!),
		queryKey: queryKeys.clubPortal.availabilityForUser(clubId ?? "", userId ?? ""),
	});
}

// A write replaces the whole pattern, so every availability read for this
// club is stale afterwards, the heatmap included.
function invalidateAvailability(clubId: string) {
	return ["club-portal", "clubs", clubId, "availability"] as const;
}

export function useSetMyClubAvailability(clubId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: SetAvailabilityPattern) => clubApi.memberAvailability.setMine(clubId, data),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: invalidateAvailability(clubId) }),
	});
}

export function useSetMemberClubAvailability(clubId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ userId, data }: { userId: string; data: SetAvailabilityPattern }) =>
			clubApi.memberAvailability.setForUser(clubId, userId, data),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: invalidateAvailability(clubId) }),
	});
}
